import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Lock, Check, AlertCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) setError('Invalid or missing reset link.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Password must be at least 8 characters.');
    if (password !== confirm) return setError('Passwords do not match.');

    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/confirm`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="reset-success-page">
        <div className="w-full max-w-sm text-center space-y-4">
          <div className="h-14 w-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto">
            <Check size={28} className="text-emerald-400" />
          </div>
          <h1 className="text-xl font-bold text-foreground">Password Reset</h1>
          <p className="text-sm text-muted-foreground">Your password has been updated. You can now log in.</p>
          <Button onClick={() => navigate('/login')} className="bg-emerald-500 hover:bg-emerald-600 text-white w-full h-10" data-testid="reset-go-to-login">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="h-12 w-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-3">
            <Lock size={22} className="text-emerald-400" />
          </div>
          <h1 className="text-xl font-bold text-foreground">Set a new password</h1>
          <p className="text-xs text-muted-foreground mt-1">Choose a strong password for your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">New Password</label>
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="w-full h-10 rounded-lg bg-card border border-border text-foreground text-sm px-3 pr-10 focus:outline-none focus:border-emerald-500/50 placeholder:text-muted-foreground"
                data-testid="reset-new-password" />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Confirm Password</label>
            <input type={showPw ? 'text' : 'password'} value={confirm} onChange={e => setConfirm(e.target.value)}
              placeholder="Re-enter your password"
              className="w-full h-10 rounded-lg bg-card border border-border text-foreground text-sm px-3 focus:outline-none focus:border-emerald-500/50 placeholder:text-muted-foreground"
              data-testid="reset-confirm-password" />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="reset-error">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <Button type="submit" disabled={loading || !token}
            className="w-full h-10 bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="reset-submit-btn">
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>

        <p className="text-center mt-4">
          <button onClick={() => navigate('/login')} className="text-xs text-muted-foreground hover:text-emerald-400 transition-colors">
            Back to login
          </button>
        </p>
      </div>
    </div>
  );
}
